import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../../contexts/hooks';
import { selectOrdersPermissions, selectModuleRemisions, selectModuleBarcode, selectModuleBilling, selectStateStudyNewOrder } from '../../../../contexts/selectors';
import { formatMessage } from 'devextreme/localization';
import Toolbar, { Item } from 'devextreme-react/toolbar';
import DataSource from 'devextreme/data/data_source';
import Datagrid, { Column, Selection, Lookup as LookupGrid, GroupPanel, Grouping, Scrolling, Paging } from 'devextreme-react/data-grid';
import { handleStatus, handleForwarded } from './Functions/Studies';
import { NewTemporalRemission, newToast, LabRefChip } from '../../..';
import ModalAdditionalFields from './components/ModalAdditionalFields';
import { ContextMenu, ScrollView, Tooltip, Popup as PopupComponent, SelectBox } from 'devextreme-react';
import { ToolbarItem } from 'devextreme-react/popup';
import Button from 'devextreme-react/button';
import { ordPending, ordCheck, ordFactureItem, ordDeleteStudy, ordBarcode, ordCounterExams, ordViewStudiesDetail, ordViewStudiesDetailCheck, ordForwardedYes, Pin, UnPin, ordMuestras } from '../../../../images/icons';
import { ResultEstadoResult, StudiesAddType, SysIResultados } from '../../../../types/types.orders';
import { addStudy, updateStateAndRem, updateLabRef, setOrdEstRpmData, removeStudy, setPanelVisible, setPanelPined } from '../../../../contexts/slices/orders';
import CustomStore from 'devextreme/data/custom_store';
import { useScreenSize } from '../../../../utils/media-query';
import { ModalConfirmation } from '../../..';
import useApiRem from '../../../../api/api.remmisions';
import { UseApiResults } from '../../../../api/api.results';
import { useApiOrders } from '../../../../api/api.orders';
import { shallowEqual } from 'react-redux';
import { useApiFunctionsResult } from './Functions/Results';
import { StudiesSearchSelect, ProfilesSearchSelect } from './components';
import type { StudyItem, ProfileItem } from './components';
import { SampleTubesPanel, TmBcListDTO } from './components/SampleTubesPanel';
import moment from 'moment';
import { useActiveTab } from '../../../../hooks/useActiveTab';

type TemporalRemInfo = { labRefId: number; endDate: string | null };

const Index = () => {
  //#region DECLARACIONES Y VARIABLES
  const { isSmall, isXSmall } = useScreenSize();
  // Tab 1 = Estudios. Solo consulta al servidor mientras es la pestaña visible.
  const isActiveTab = useActiveTab(1);
  const idOrd = useAppSelector((x) => x.globalOrders.CurrentOrder.IdOrder);
  const mode = useAppSelector((x) => x.globalOrders.CurrentOrder.Mode);
  const currentData = useAppSelector((x) => x.globalOrders.CurrentOrder.OrderData);

  // Selectores consolidados optimizados
  const permissions = useAppSelector(selectOrdersPermissions);
  const ModuleRemisions = useAppSelector(selectModuleRemisions);
  const ModuleBarcode = useAppSelector(selectModuleBarcode);
  const ModuleBilling = useAppSelector(selectModuleBilling);
  const StateStudyNewOrder = useAppSelector(selectStateStudyNewOrder);

  const infoAditionalFields = useAppSelector((x) => x.configuration.Data?.SystemFields, shallowEqual)
    ?.filter((x) => x.Dfc_Tabla === 'Estudios' && x.Dfc_Estado === true)
    .sort((a, b) => a.Dfc_Orden - b.Dfc_Orden);

  // Aliases para compatibilidad
  const Prm_Ord_OEr = permissions.canEditOrders;
  const Prm_Ord_Remisiones_Tmp = permissions.canCreateTemporalRemissions;
  const Prm_Ord_Remisiones_ChangeStudies = permissions.canChangeStudiesRemissions;
  const Prm_CodBarImprimir = permissions.canPrintBarcodes;

  const Areas = useAppSelector((x) => x.configuration.Data?.Areas);
  const cmbLabRef = useAppSelector((x) => x.configuration.Data?.LabReferencia) || [];

  // Datos necesarios para manejar perfiles
  const profiles = useAppSelector((x) => x.configuration.Data?.Perfiles || [], shallowEqual);

  const dataGridRef = useRef<Datagrid>(null);
  const dataGridProfRef = useRef<Datagrid>(null);
  const isPrintingRef = useRef<boolean>(false);
  const { getResults } = UseApiResults();
  const [selectedStudie, setSelectedStudie] = useState<{ state: boolean; id: number } | null>(null);
  const addedStudies = useAppSelector((x) => x.globalOrders.CurrentOrder.StudiesData);
  const addedStudiesRef = useRef(addedStudies);
  addedStudiesRef.current = addedStudies;
  const dispatch = useAppDispatch();

  // APIs necesarias para los handlers
  const { getTemporalRemisions, UpdateLabRefStudy, checkStudyInRemission } = useApiRem();
  const { GetSampleTypeAndBarcode } = useApiOrders();

  // Remisiones temporales vigentes, indexadas por estudio: el laboratorio de la remisión
  // sustituye al del estudio y se distingue en el chip de LAB.REF
  const [temporalRemByStudy, setTemporalRemByStudy] = useState<Map<number, TemporalRemInfo>>(new Map());
  const getTemporalRemisionsRef = useRef(getTemporalRemisions);
  getTemporalRemisionsRef.current = getTemporalRemisions;

  // La remisión temporal aplica a la orden solo si su creación cae dentro del rango de la
  // remisión; para una orden que aún no existe, el momento de referencia es ahora
  const orderDate = currentData?.Ord_FechaCreacion;

  // Regla del panel: cada pestaña consulta solo mientras está activa. Esta pestaña queda
  // montada tras visitarse, y sin la guarda seguía pidiendo remisiones y tubos en cada
  // cambio de orden mientras el usuario estaba en Resultados. Al volver a activarse, los
  // efectos se reejecutan (isActiveTab está en sus dependencias) y se ponen al día.
  useEffect(() => {
    if (!ModuleRemisions || !isActiveTab) return;

    getTemporalRemisionsRef.current().then((res: any) => {
      if (!Array.isArray(res)) {
        setTemporalRemByStudy(new Map());
        return;
      }

      const referenceDate = idOrd !== -1 && orderDate ? new Date(orderDate) : new Date();

      const map = new Map<number, TemporalRemInfo>();
      res.forEach((rem: any) => {
        if (!rem?.Rmt_Es_Id || !rem?.Rmt_Lr_Id) return;

        const start = rem.Rmt_FechaInicio ? new Date(rem.Rmt_FechaInicio) : null;
        const end = rem.Rmt_FechaFin ? new Date(rem.Rmt_FechaFin) : null;
        if ((start && referenceDate < start) || (end && referenceDate > end)) return;

        map.set(rem.Rmt_Es_Id, { labRefId: rem.Rmt_Lr_Id, endDate: rem.Rmt_FechaFin ?? null });
      });
      setTemporalRemByStudy(map);
    });
  }, [ModuleRemisions, idOrd, orderDate, isActiveTab]);

  // Laboratorio con el que cada estudio quedó guardado en la orden. No sirve mirar
  // Accion, porque el servidor devuelve los estudios con el mismo valor con el que se
  // enviaron; se toma la foto del estado guardado cada vez que la orden está en 'view'.
  const [savedLabRefByStudy, setSavedLabRefByStudy] = useState<Map<number, number | null>>(new Map());

  useEffect(() => {
    if (idOrd === -1 || mode !== 'view') return;

    const map = new Map<number, number | null>();
    addedStudies.forEach((study) => map.set(study.Es_Id, study.Es_Lr_Id ?? null));
    setSavedLabRefByStudy(map);
  }, [idOrd, mode, addedStudies]);

  // Los grids usan repaintChangesOnly: al llegar las remisiones hay que repintarlos para
  // que el chip de LAB.REF refleje el laboratorio temporal
  useEffect(() => {
    dataGridRef.current?.instance.repaint();
    dataGridProfRef.current?.instance.repaint();
  }, [temporalRemByStudy, savedLabRefByStudy]);

  // Avisar una sola vez por orden si alguna remisión temporal quedará descartada porque el
  // estudio ya tiene guardado otro laboratorio de referencia (ese tiene prioridad)
  const warnedOrderRef = useRef<number | null | undefined>(null);

  useEffect(() => {
    if (idOrd === -1 || temporalRemByStudy.size === 0 || savedLabRefByStudy.size === 0) return;
    if (warnedOrderRef.current === idOrd) return;

    const ignored = addedStudies.filter((study) => {
      const temporalRem = temporalRemByStudy.get(study.Es_Id);
      const savedLabRef = savedLabRefByStudy.get(study.Es_Id);
      return !!temporalRem && savedLabRef != null && savedLabRef !== temporalRem.labRefId;
    });

    if (ignored.length === 0) return;

    warnedOrderRef.current = idOrd;
    const nombres = ignored.map((study) => study.Es_Nombre).join(', ');
    newToast('WARNING', `Hay una remisión temporal vigente para ${nombres}, pero no se aplicará: el estudio ya tiene un laboratorio de referencia asignado, que tiene prioridad.`, 8);
  }, [idOrd, temporalRemByStudy, savedLabRefByStudy, addedStudies]);

  // Estado para tubos/muestras
  const [sampleTubesData, setSampleTubesData] = useState<TmBcListDTO[]>([]);
  const [muestrasPopupVisible, setMuestrasPopupVisible] = useState<boolean>(false);
  const [samplePanelVisible, setSamplePanelVisible] = useState<boolean>(true);

  // Estados y constantes para componentes genéricos
  const [modalRemVisible, setModalRemVisible] = useState<boolean>(false);
  const [modalRemConfVisible, setModalRemConfVisible] = useState<boolean>(false);
  const [modalLabRefVisible, setModalLabRefVisible] = useState<boolean>(false);
  const [selectedLabRef, setSelectedLabRef] = useState<number | null>(null);
  const [modalEditLabRefVisible, setModalEditLabRefVisible] = useState<boolean>(false);
  const [editingStudy, setEditingStudy] = useState<{ id: number; name: string; currentLabRefId: number | null } | null>(null);
  const [newLabRefForStudy, setNewLabRefForStudy] = useState<number | null>(null);
  // Estado para modal de campos adicionales personalizado
  const [modalAdditionalFieldsVisible, setModalAdditionalFieldsVisible] = useState<boolean>(false);
  const [studyForAdditionalFields, setStudyForAdditionalFields] = useState<StudiesAddType | null>(null);
  // Estado para modal de eliminación de estudio perteneciente a un perfil
  const [deleteProfileModal, setDeleteProfileModal] = useState<{ studyId: number; profileId: number; profileName: string } | null>(null);
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const { handlePrintBarcodesLabel } = useApiFunctionsResult();
  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };

  // Recargar los DataGrid cuando cambian los estudios (sin recrear el DataSource)
  useEffect(() => {
    customSrcEstudios.reload();
    dataGridProfRef.current?.instance.refresh();
  }, [addedStudies]);

  // Efecto para cargar tubos/muestras cada vez que cambian los estudios
  const studyIdsKey = useMemo(() => {
    return addedStudies
      .map((s) => s.Es_Id)
      .sort((a, b) => a - b)
      .join(',');
  }, [addedStudies]);

  useEffect(() => {
    if (!isActiveTab) return;
    if (!studyIdsKey) {
      setSampleTubesData([]);
      return;
    }
    const studyIds = studyIdsKey.split(',').map(Number);
    GetSampleTypeAndBarcode(studyIds).then((res: TmBcListDTO[]) => {
      setSampleTubesData(res || []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyIdsKey, isActiveTab]);

  // Convertir estudios agregados a formato StudyItem para evitar duplicados
  const addedStudiesAsItems = useMemo((): StudyItem[] => {
    return addedStudies.map((study) => ({
      id: study.Es_Id,
      idlr: study.Es_Lr_Id || null,
      idAr: study.Es_Aa_Id,
      code: study.Es_Codigo,
      name: study.Es_Nombre,
      group: 'Estudios', // Campo genérico por ahora
      isActive: true,
    }));
  }, [addedStudies]);

  // Handler para selección de estudio con nuevo componente
  const handleStudySelected = useCallback(
    async (study: StudyItem | null) => {
      if (!study) {
        return; // No hacer nada si se limpia la selección
      }
      try {
        if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
          newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
          return;
        }
        const studyToAdd: StudiesAddType = {
          Es_Id: study.id as number,
          Es_Lr_Id: study.idlr as number,
          Es_Codigo: study.code,
          Es_Aa_Id: study.idAr as number,
          Es_Nombre: study.name,
          Estado: true,
          Remitido: false,
          Accion: 1, // Acción de agregar
          Editado: false,
        };

        dispatch(addStudy(studyToAdd));
        newToast('SUCCESS', `Estudio '${study.name}' agregado a la orden`, 3);
      } catch (error) {
        console.error('Error adding study to order:', error);
        newToast('ERROR', 'Error al agregar el estudio a la orden', 3);
      }
    },
    [currentData, dispatch]
  );

  // Handler para selección de perfil con nuevo componente
  const handleProfileSelected = useCallback(
    async (profile: ProfileItem | null) => {
      if (!profile) {
        return; // No hacer nada si se limpia la selección
      }
      try {
        let selectedProfile: any;

        if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
          newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
          return;
        }

        // Si tenemos los datos originales del perfil, usarlos directamente
        if (profile.originalData) {
          selectedProfile = profile.originalData;
        } else {
          // Fallback: agrupar perfiles por Rpe_Pfe_Id como en el hook original
          const profilesGrouped =
            profiles?.reduce((acumulador: any, actual) => {
              const { Rpe_Pfe_Id, SysI_Estudios, SysI_PerfilesEstudios, ...resto } = actual;
              const objetoAgrupado = acumulador.find((elem) => elem.Rpe_Pfe_Id === Rpe_Pfe_Id);

              if (objetoAgrupado) {
                objetoAgrupado.SysI_Estudios.push(SysI_Estudios);
              } else {
                acumulador.push({
                  Rpe_Pfe_Id,
                  SysI_PerfilesEstudios,
                  SysI_Estudios: [SysI_Estudios],
                });
              }
              return acumulador;
            }, []) || [];

          selectedProfile = profilesGrouped.find((p: any) => p.Rpe_Pfe_Id === profile.id);
        }

        if (!selectedProfile) {
          newToast('ERROR', 'Perfil no encontrado', 3);
          return;
        }

        // Verificar si el perfil ya fue agregado
        if (addedStudies.some((x) => x.Rpe_Pfe_Id === selectedProfile.Rpe_Pfe_Id)) {
          newToast('INFO', `El perfil '${selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre}' ya ha sido agregado`, 5);
          return;
        }

        // Agregar todos los estudios del perfil
        for (const element of selectedProfile.SysI_Estudios) {
          if (addedStudies.some((x) => x.Es_Id === element.Es_Id)) {
            newToast('INFO', `El estudio '${element.Es_Nombre}' ya ha sido agregado`, 3);
            continue;
          }

          const studyToAdd: StudiesAddType = {
            Estado: StateStudyNewOrder,
            Es_Id: element.Es_Id,
            Es_Codigo: element.Es_Codigo,
            Es_Nombre: element.Es_Nombre,
            Es_Lr_Id: element.Es_Lr_Id,
            Es_Aa_Id: element.Es_Aa_Id || null,
            Rpe_Pfe_Id: selectedProfile.Rpe_Pfe_Id,
            Remitido: false,
            Accion: 1,
            Editado: false,
            Profile: selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre,
          };

          // Verificar remisiones temporales
          try {
            const resp = await getTemporalRemisions(element.Es_Id);
            if (resp) {
              studyToAdd.Remitido = true;
            }
          } catch (error) {
            console.warn('Error checking temporal remissions:', error);
          }

          dispatch(addStudy(studyToAdd));
        }

        newToast('SUCCESS', `Perfil '${selectedProfile.SysI_PerfilesEstudios.Pfe_Nombre}' agregado con ${selectedProfile.SysI_Estudios.length} estudios`, 3);
      } catch (error) {
        console.error('Error adding profile to order:', error);
        newToast('ERROR', 'Error al agregar el perfil a la orden', 3);
      }
    },
    [currentData, profiles, addedStudies, StateStudyNewOrder, getTemporalRemisions, dispatch]
  );
  //#endregion

  //#region HANDLERS PARA LA FUNCIONALIDAD PRINCIPAL
  const handleSelectionChange = (e) => {
    dataGridProfRef.current?.instance.clearSelection();
    const selected = dataGridRef.current?.instance.getSelectedRowsData();
    if (selected && selected.length > 0) {
      setSelectedStudie({ state: selected[0].Estado, id: e.selectedRowKeys[0] });
    }
  };

  const handleSelectionChangeProfile = (e) => {
    dataGridRef.current?.instance.clearSelection();
    const selected = dataGridProfRef.current?.instance.getSelectedRowsData();
    if (selected && selected.length > 0) {
      setSelectedStudie({ state: selected[0].Estado, id: e.selectedRowKeys[0] });
    }
  };

  const handleClicChangeStatus = () => {
    if (selectedStudie?.id) {
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'State', state: undefined }));
    }
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
    setSelectedStudie(null);
  };

  const handleClicChangeForwared = async () => {
    if (selectedStudie?.id) {
      const currentStudy = addedStudies.find((x) => x.Es_Id === selectedStudie.id);

      // Si el estudio NO está remitido y hay módulo de remisiones y permisos
      if (!currentStudy?.Remitido && ModuleRemisions && Prm_Ord_Remisiones_Tmp) {
        // Siempre mostrar el modal para elegir entre remisión temporal o única
        setModalRemConfVisible(true);
        return;
      }

      // Si ya está remitido o no hay permisos, solo cambiar el estado
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'Rem', state: undefined }));
    }
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
    setSelectedStudie(null);
  };

  // Ejecuta la eliminación de uno o varios estudios, validando preorden y resultados ingresados
  const executeDeleteStudies = (studyIds: number[]) => {
    if (studyIds.length === 0) {
      return;
    }
    if (idOrd !== -1) {
      // Si la orden tiene el campo Ord_POrd_Id y el valor es diferente de 0, significa que la orden fue creada desde una preorden, por lo que no se puede editar.
      if (currentData && currentData.Ord_POrd_Id && currentData.Ord_POrd_Id !== 0 && currentData.Ord_POrd_Id !== null) {
        newToast('WARNING', 'No es posible eliminar ni agregar estudios a una orden que fué creada desde una preorden', 5);
        return;
      }
      getResults().then((x) => {
        const result: SysIResultados[] = x;
        if (result.filter((r) => studyIds.includes(r.Res_Es_Id) && r.Res_Estado > ResultEstadoResult.SinResultado).length > 0) {
          newToast('WARNING', 'El estudio seleccionado ya cuenta con resultados ingresados.', 5);
          return;
        }
        studyIds.forEach((id) => dispatch(removeStudy(id)));
      });
    } else {
      studyIds.forEach((id) => dispatch(removeStudy(id)));
    }
  };

  const handleClickDeleteStudy = () => {
    if (!selectedStudie?.id) {
      return;
    }
    const study = addedStudies.find((x) => x.Es_Id === selectedStudie.id);
    // Si el estudio pertenece a un perfil, preguntar si se elimina el perfil completo o solo el estudio
    if (study?.Rpe_Pfe_Id) {
      setDeleteProfileModal({ studyId: study.Es_Id, profileId: study.Rpe_Pfe_Id, profileName: study.Profile || '' });
      return;
    }
    executeDeleteStudies([selectedStudie.id]);
  };

  const handleDeleteOnlyStudy = () => {
    if (deleteProfileModal) {
      executeDeleteStudies([deleteProfileModal.studyId]);
    }
    setDeleteProfileModal(null);
    setSelectedStudie(null);
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
  };

  const handleDeleteWholeProfile = () => {
    if (deleteProfileModal) {
      const ids = addedStudies.filter((x) => x.Rpe_Pfe_Id === deleteProfileModal.profileId).map((x) => x.Es_Id);
      executeDeleteStudies(ids);
    }
    setDeleteProfileModal(null);
    setSelectedStudie(null);
    dataGridRef.current?.instance.clearSelection();
    dataGridProfRef.current?.instance.clearSelection();
  };

  const handleCancelDeleteProfile = () => {
    setDeleteProfileModal(null);
  };

  const handleUpdateRow = (e) => {};

  const updateFields = useCallback(
    (key, values) => {
      return new Promise((resolve) => {
        const newStudies = addedStudiesRef.current.map((study) => {
          if (study.Es_Id === key) {
            return { ...study, ...values };
          }
          return study;
        });
        Object.keys(values.AditionalFields).forEach((element) => {
          dispatch(setOrdEstRpmData({ NomParam: element, IdEst: key, Value: values.AditionalFields[element] }));
        });
        resolve(newStudies);
      });
    },
    [dispatch]
  );

  const customDataSource = useMemo(
    () =>
      new CustomStore({
        key: 'Es_Id',
        loadMode: 'raw',
        load: () => {
          return new Promise((resolve) => {
            const studies = addedStudiesRef.current.filter((x) => !x.Rpe_Pfe_Id);
            resolve(studies);
          });
        },
        update: (key, values) => {
          return updateFields(key, values);
        },
      }),
    [updateFields]
  );

  const customSrcEstudios = useMemo(
    () =>
      new DataSource({
        store: customDataSource,
        reshapeOnPush: true,
      }),
    [customDataSource]
  );

  const customDataSourceProfiles = useMemo(
    () =>
      new CustomStore({
        key: 'Es_Id',
        load: () => {
          return new Promise((resolve) => {
            const studies = addedStudiesRef.current.filter((x) => x.Rpe_Pfe_Id);
            resolve(studies);
          });
        },
        update: (key, values) => {
          return updateFields(key, values);
        },
      }),
    [updateFields]
  );

  const setModalRemmVisible = () => {
    setModalRemVisible(false);
    setModalRemConfVisible(false);
  };

  const onConfirmRemmisionModal = () => {
    setModalRemVisible(true);
  };

  const onNoConfirmRemmisionModal = async () => {
    setModalRemConfVisible(false);

    if (selectedStudie?.id && idOrd && idOrd !== -1) {
      const existing = await checkStudyInRemission(idOrd, selectedStudie.id);
      if (existing) {
        const fechaFormateada = existing.Rmd_Fecha ? moment(existing.Rmd_Fecha).format('DD/MM/YYYY hh:mm A') : '';
        newToast('INFO', `Este estudio ya fue remisionado el día ${fechaFormateada} en la remisión '${existing.Rmd_Rm_Id}'`, 6);
        return;
      }
    }

    // Abrir popup para seleccionar laboratorio de referencia
    setModalLabRefVisible(true);
  };

  const onConfirmLabRefSelection = () => {
    if (selectedStudie && selectedLabRef) {
      // Actualizar el laboratorio de referencia y marcar como remitido
      dispatch(updateLabRef({ Es_Id: selectedStudie.id, Es_Lr_Id: selectedLabRef }));
      newToast('SUCCESS', 'Laboratorio de referencia actualizado correctamente', 3);
    }
    // Cerrar modal y limpiar selección
    setModalLabRefVisible(false);
    setSelectedLabRef(null);
  };

  const onCancelLabRefSelection = () => {
    setModalLabRefVisible(false);
    setSelectedLabRef(null);
  };

  const handleChipClick = (studyId: number, studyName: string, currentLabRefId: number | null) => {
    if (Prm_Ord_Remisiones_ChangeStudies) {
      setEditingStudy({ id: studyId, name: studyName, currentLabRefId });
      setNewLabRefForStudy(currentLabRefId);
      setModalEditLabRefVisible(true);
    }
  };

  const handleSaveLabRefChange = async () => {
    if (!editingStudy) {
      return;
    }

    try {
      const result = await UpdateLabRefStudy(editingStudy.id, newLabRefForStudy);
      if (result) {
        // Actualizar el laboratorio de referencia en el estudio en Redux
        dispatch(updateLabRef({ Es_Id: editingStudy.id, Es_Lr_Id: newLabRefForStudy }));
        newToast('SUCCESS', 'Laboratorio de referencia actualizado correctamente', 3);
        setModalEditLabRefVisible(false);
        setEditingStudy(null);
        setNewLabRefForStudy(null);
      } else {
        newToast('ERROR', 'Error al actualizar el laboratorio de referencia', 3);
      }
    } catch (error) {
      console.error('Error updating lab ref:', error);
      newToast('ERROR', 'Error al actualizar el laboratorio de referencia', 3);
    }
  };

  const handleCancelLabRefChange = () => {
    setModalEditLabRefVisible(false);
    setEditingStudy(null);
    setNewLabRefForStudy(null);
  };

  const setConfigTemRem = (isSaved: boolean, lrId?: number | null) => {
    if (isSaved && selectedStudie) {
      dispatch(updateStateAndRem({ Es_Id: selectedStudie.id, type: 'Rem', state: true }));

      // SIEMPRE actualizar el laboratorio de referencia si se proporcionó
      if (lrId) {
        dispatch(updateLabRef({ Es_Id: selectedStudie.id, Es_Lr_Id: lrId }));
      }

      // Cerrar el modal automáticamente después de guardar
      setModalRemVisible(false);
      newToast('SUCCESS', 'Remisión temporal guardada correctamente', 3);
    }
  };

  const handlePrintBarcode = useCallback(async () => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }

    // Prevenir doble clic
    if (isPrintingRef.current) {
      return;
    }

    try {
      isPrintingRef.current = true;
      await handlePrintBarcodesLabel(idOrd);
    } finally {
      // Resetear después de un pequeño delay para evitar clics rápidos
      setTimeout(() => {
        isPrintingRef.current = false;
      }, 500);
    }
  }, [idOrd, handlePrintBarcodesLabel, Prm_CodBarImprimir]);

  const handlePrintBarcodeSelected = useCallback(async () => {
    if (!idOrd || idOrd === -1) {
      return;
    }
    if (!Prm_CodBarImprimir) {
      newToast('INFO', 'No cuenta con los permisos para imprimir etiquetas', 5);
      return;
    }

    // Prevenir doble clic
    if (isPrintingRef.current) {
      return;
    }

    if (selectedStudie?.id) {
      try {
        isPrintingRef.current = true;
        const LstIdsEstudios = [selectedStudie.id];
        await handlePrintBarcodesLabel(idOrd, LstIdsEstudios);
      } finally {
        setTimeout(() => {
          isPrintingRef.current = false;
        }, 500);
      }
    }
  }, [idOrd, selectedStudie?.id, handlePrintBarcodesLabel, Prm_CodBarImprimir]);

  // Handler para abrir el modal de campos adicionales
  const handleOpenAdditionalFieldsModal = useCallback((study: StudiesAddType) => {
    setStudyForAdditionalFields(study);
    setModalAdditionalFieldsVisible(true);
  }, []);

  // Handler para cerrar el modal de campos adicionales
  const handleCloseAdditionalFieldsModal = useCallback(() => {
    setModalAdditionalFieldsVisible(false);
    setStudyForAdditionalFields(null);
  }, []);

  // Handler para guardar campos adicionales (con opción de aplicar a otros estudios)
  const handleSaveAdditionalFields = useCallback(
    (studyId: number, fieldsData: Record<string, any>, applyToOthers?: { fieldName: string; value: any }[]) => {
      // Guardar campos para el estudio actual
      Object.keys(fieldsData).forEach((fieldName) => {
        dispatch(setOrdEstRpmData({ NomParam: fieldName, IdEst: studyId, Value: fieldsData[fieldName] }));
      });

      // Si se indicó aplicar a otros estudios
      if (applyToOthers && applyToOthers.length > 0) {
        // Obtener todos los otros estudios
        const otherStudies = addedStudies.filter((s) => s.Es_Id !== studyId);

        for (const study of otherStudies) {
          for (const fieldToApply of applyToOthers) {
            // Solo aplicar si el campo está vacío en este estudio
            const currentValue = study.AditionalFields?.[fieldToApply.fieldName];
            if (currentValue === undefined || currentValue === null || currentValue === '') {
              dispatch(
                setOrdEstRpmData({
                  NomParam: fieldToApply.fieldName,
                  IdEst: study.Es_Id,
                  Value: fieldToApply.value,
                })
              );
            }
          }
        }
      }

      // Refrescar los datasources
      dataGridRef.current?.instance.refresh();
      dataGridProfRef.current?.instance.refresh();
    },
    [dispatch, addedStudies]
  );

  // Función para verificar si un estudio tiene campos adicionales llenos
  const hasFilledAdditionalFields = useCallback(
    (study: StudiesAddType): boolean => {
      if (!study.AditionalFields || !infoAditionalFields) return false;

      const dynamicFields = infoAditionalFields.filter((f) => f.Dfc_Dinamico);
      return dynamicFields.some((field) => {
        const value = study.AditionalFields?.[field.Dfc_Equivalencia];
        return value !== undefined && value !== null && value !== '';
      });
    },
    [infoAditionalFields]
  );

  // Renderizar celda de botón con icono dinámico
  const renderAdditionalFieldsButton = useCallback(
    (cellData: any) => {
      const study = cellData.data as StudiesAddType;
      const hasFilled = hasFilledAdditionalFields(study);

      return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
          <img
            src={hasFilled ? ordViewStudiesDetailCheck : ordViewStudiesDetail}
            alt={formatMessage('OrdViewAdditionalInfForStudy')}
            title={formatMessage('OrdViewAdditionalInfForStudy')}
            onClick={() => handleOpenAdditionalFieldsModal(study)}
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              opacity: 0.8,
            }}
          />
        </div>
      );
    },
    [hasFilledAdditionalFields, handleOpenAdditionalFieldsModal]
  );
  //#endregion

  return (
    <>
      <ModalConfirmation onConfirm={onConfirmRemmisionModal} onCancel={onNoConfirmRemmisionModal} visible={modalRemConfVisible} buttons='No/Yes' message={formatMessage('OrdConfirmationReferStudyTemporaly')} />
      <NewTemporalRemission idEst={selectedStudie?.id || -1} title={''} visible={modalRemVisible} setVisible={setModalRemmVisible} setConfigTemRem={setConfigTemRem} />

      {/* Modal personalizado para campos adicionales */}
      <ModalAdditionalFields visible={modalAdditionalFieldsVisible} onHide={handleCloseAdditionalFieldsModal} study={studyForAdditionalFields} fields={infoAditionalFields || []} allStudies={addedStudies} onSave={handleSaveAdditionalFields} />

      {/* Popup para elegir entre eliminar el perfil completo o solo el estudio seleccionado */}
      <PopupComponent visible={deleteProfileModal !== null} onHiding={handleCancelDeleteProfile} dragEnabled={false} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title='Eliminar estudio de un perfil' width={isXSmall || isSmall ? '100%' : 500} height='auto' fullScreen={isXSmall}>
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '10px' }}>
            El estudio seleccionado pertenece al perfil <strong>{deleteProfileModal?.profileName}</strong>.
          </p>
          <p style={{ marginBottom: '0' }}>¿Desea eliminar el perfil completo o únicamente el estudio seleccionado?</p>
        </div>
        <ToolbarItem toolbar='bottom' location='after'>
          <div className={`form-popup-buttons-container ${isXSmall || isSmall ? 'flex-buttons' : ''}`}>
            <Button text='Solo el estudio' stylingMode='contained' onClick={handleDeleteOnlyStudy} />
            <Button text='Eliminar perfil completo' stylingMode='contained' type='default' onClick={handleDeleteWholeProfile} />
          </div>
        </ToolbarItem>
      </PopupComponent>

      {/* Popup para seleccionar laboratorio de referencia */}
      <PopupComponent visible={modalLabRefVisible} onHiding={onCancelLabRefSelection} dragEnabled={false} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title='Seleccionar Laboratorio de Referencia' width={isXSmall || isSmall ? '100%' : 500} height='auto' fullScreen={isXSmall}>
        <div style={{ padding: '20px' }}>
          <p style={{ marginBottom: '20px' }}>Seleccione el laboratorio de referencia al cual desea remitir este estudio:</p>
          <SelectBox dataSource={cmbLabRef} displayExpr='Lr_Nombre' valueExpr='Lr_Id' value={selectedLabRef} onValueChanged={(e) => setSelectedLabRef(e.value)} placeholder='Seleccione un laboratorio...' searchEnabled={true} searchMode='contains' searchExpr='Lr_Nombre' showClearButton={true} stylingMode='outlined' />
        </div>
        <ToolbarItem toolbar='bottom' location='after'>
          <div className='form-popup-buttons-container'>
            <Button text='Cancelar' stylingMode='contained' onClick={onCancelLabRefSelection} />
            <Button text='Confirmar' stylingMode='contained' type='default' disabled={!selectedLabRef} onClick={onConfirmLabRefSelection} />
          </div>
        </ToolbarItem>
      </PopupComponent>

      {/* Popup para editar laboratorio de referencia del estudio */}
      <PopupComponent visible={modalEditLabRefVisible} onHiding={handleCancelLabRefChange} dragEnabled={false} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title='Cambiar Laboratorio de Referencia' width={isXSmall || isSmall ? '100%' : 500} height='auto' fullScreen={isXSmall}>
        <div style={{ padding: '20px' }}>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Estudio:</label>
            <div style={{ padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>{editingStudy?.name}</div>
          </div>
          <div style={{ marginBottom: '15px' }}>
            <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Laboratorio de Referencia:</label>
            <SelectBox dataSource={cmbLabRef} displayExpr='Lr_Nombre' valueExpr='Lr_Id' value={newLabRefForStudy} onValueChanged={(e) => setNewLabRefForStudy(e.value)} placeholder='Seleccione un laboratorio...' searchEnabled={true} searchMode='contains' searchExpr='Lr_Nombre' showClearButton={true} stylingMode='outlined' />
          </div>
        </div>
        <ToolbarItem toolbar='bottom' location='after'>
          <div className='form-popup-buttons-container'>
            <Button text='Cancelar' stylingMode='contained' onClick={handleCancelLabRefChange} />
            <Button text='Guardar' stylingMode='contained' type='default' disabled={newLabRefForStudy === editingStudy?.currentLabRefId} onClick={handleSaveLabRefChange} />
          </div>
        </ToolbarItem>
      </PopupComponent>

      <div style={{ height: 'calc(100vh - 280px)', paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
        <div className='view-wrapper view-wrapper-dashboard'>
          <Toolbar>
            <Item location='before'>
              <StudiesSearchSelect onStudySelected={handleStudySelected} selectedStudies={addedStudiesAsItems} placeholder='Buscar estudios...' label={formatMessage('OrdEstAdd')} disabled={mode === 'loading' || mode === 'view'} className='comboboxItems' minSearchLength={1} />
            </Item>
            <Item location='before'>
              <ProfilesSearchSelect onProfileSelected={handleProfileSelected} selectedProfiles={[]} placeholder='Buscar perfiles...' label={formatMessage('OrdProfilesAdd')} disabled={mode === 'loading' || mode === 'view'} className='comboboxItems' minSearchLength={1} />
            </Item>
            {ModuleBilling && (
              <>
                <Item location='after' locateInMenu='auto'>
                  <div className='separator' />
                </Item>
                <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
                  <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdInvoiceItem' icon={ordFactureItem} stylingMode='text' text='Facturar' />
                </Item>
              </>
            )}
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdDeleteSelectedStudy' icon={ordDeleteStudy} stylingMode='text' onClick={handleClickDeleteStudy} text='Delete' />
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} id='OrdMarkSelectedStudyAsPending' onClick={handleClicChangeStatus} icon={selectedStudie && selectedStudie.state ? ordPending : ordCheck} text='Pending' stylingMode='text' />
            </Item>
            <Item location='after' locateInMenu='auto'>
              <div className='separator' />
            </Item>
            <Item location='after' visible={Prm_CodBarImprimir && ModuleBarcode} widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={idOrd === -1 || mode !== 'view'} icon={ordBarcode} id='OrdPrintBarcode' stylingMode='text' text='Imprimir etiquetas' onClick={handlePrintBarcode} />
              <ContextMenu width={'auto'} target={'#OrdPrintBarcode'} dataSource={[{ text: 'Imprimir estudios seleccionados', icon: ordBarcode }]} onItemClick={handlePrintBarcodeSelected} />
              <Tooltip target={'#OrdPrintBarcode'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                Imprimir etiquetas para la orden
              </Tooltip>
            </Item>
            <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
              <Button disabled={selectedStudie === null || mode === 'loading' || mode === 'view'} icon={ordForwardedYes} id='OrdReferSelectedStudy' onClick={handleClicChangeForwared} stylingMode='text' text='Remitido' />
            </Item>
            {(isSmall || isXSmall) && (
              <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
                <Button id='btnMuestrasOrd' icon={ordMuestras} text='Muestras' stylingMode='text' onClick={() => setMuestrasPopupVisible(true)} />
                <Tooltip target={'#btnMuestrasOrd'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  Ver muestras
                </Tooltip>
              </Item>
            )}
            {!(isSmall || isXSmall) && (
              <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
                <Button id='btnToggleMuestrasOrd' icon={ordMuestras} text={samplePanelVisible ? 'Ocultar muestras' : 'Muestras'} stylingMode='text' onClick={() => setSamplePanelVisible(!samplePanelVisible)} />
                <Tooltip target={'#btnToggleMuestrasOrd'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                  {samplePanelVisible ? 'Ocultar panel de muestras' : 'Ver panel de muestras'}
                </Tooltip>
              </Item>
            )}
            <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
              <Button id='showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
              <Tooltip target={'#showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
              </Tooltip>
            </Item>
          </Toolbar>
          <div style={{ marginTop: 20, display: 'flex', height: 'calc(100vh - 360px)' }}>
            <ScrollView height={'100%'} style={{ flex: '1', marginRight: '4px' }}>
              <div>
                <div id='gridContainer'>
                  {addedStudies.filter((x) => !x.Rpe_Pfe_Id).length > 0 && (
                    <Datagrid onRowUpdated={handleUpdateRow} dataSource={customSrcEstudios} ref={dataGridRef} onSelectionChanged={handleSelectionChange} repaintChangesOnly={true}>
                      <Selection mode='single' />
                      <Scrolling mode='standard' />
                      <Paging enabled={false} />
                      <GroupPanel visible={false} />
                      <Grouping autoExpandAll={true} />
                      <Column dataField='Es_Aa_Id' caption={formatMessage('OrdEstudyGridArea')} groupIndex={0} allowEditing={false}>
                        <LookupGrid dataSource={Areas} displayExpr={'Aa_Nombre'} valueExpr={'Aa_Id'} />
                      </Column>
                      <Column cellRender={handleStatus} width={80} caption={formatMessage('OrdEstudyGridState')} dataField='Estado' allowEditing={false} />
                      <Column width={80} caption={formatMessage('OrdEstudyGridCode')} dataField='Es_Codigo' allowEditing={false} />
                      <Column width={90} caption='LAB.REF' dataField='Es_Lr_Id' cellRender={(cellData) => renderLabRefChip(cellData, cmbLabRef, handleChipClick, Prm_Ord_Remisiones_ChangeStudies, temporalRemByStudy, savedLabRefByStudy)} alignment='center' allowEditing={false} />
                      <Column caption={formatMessage('OrdEstudyGridStudyName')} dataField='Es_Nombre' minWidth={200} allowEditing={false} />
                      <Column cellRender={handleForwarded} caption={formatMessage('OrdEstudyGridForwarded')} width={90} dataField='Remitido' allowEditing={false} />
                      {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && <Column caption='' width={50} alignment='center' cellRender={renderAdditionalFieldsButton} allowEditing={false} />}
                    </Datagrid>
                  )}

                  {addedStudies.filter((x) => x.Rpe_Pfe_Id).length > 0 && (
                    <>
                      <h3 style={{ textAlign: 'center', color: '#03A9F4', fontSize: '15px', marginBottom: 0, fontWeight: 'bold' }}>{formatMessage('OrdProfilesAdd').toUpperCase()}</h3>
                      <Datagrid hoverStateEnabled={true} dataSource={customDataSourceProfiles} ref={dataGridProfRef} onSelectionChanged={handleSelectionChangeProfile} repaintChangesOnly={true}>
                        <Selection mode='single' />
                        <GroupPanel visible={false} />
                        <Scrolling mode='standard' />
                        <Paging enabled={false} />
                        <Grouping autoExpandAll={true} />
                        <Column dataField='Profile' caption={formatMessage('OrdEstudyGridProfileGroup')} groupIndex={0} allowEditing={false} />
                        <Column cellRender={handleStatus} width={80} caption={formatMessage('OrdEstudyGridState')} dataField='Estado' allowEditing={false} />
                        <Column width={100} caption={formatMessage('OrdEstudyGridCode')} dataField='Es_Codigo' allowEditing={false} />
                        <Column width={90} caption='LAB.REF' dataField='Es_Lr_Id' cellRender={(cellData) => renderLabRefChip(cellData, cmbLabRef, handleChipClick, Prm_Ord_Remisiones_ChangeStudies, temporalRemByStudy, savedLabRefByStudy)} alignment='center' allowEditing={false} />
                        <Column caption={formatMessage('OrdEstudyGridStudyName')} dataField='Es_Nombre' minWidth={200} allowEditing={false} />
                        <Column visible={false} dataField='Rpe_Pfe_Id' allowEditing={false} />
                        <Column cellRender={handleForwarded} caption={formatMessage('OrdEstudyGridForwarded')} width={90} dataField='Remitido' allowEditing={false} />
                        {infoAditionalFields && infoAditionalFields.length > 0 && Prm_Ord_OEr && <Column caption='' width={50} alignment='center' cellRender={renderAdditionalFieldsButton} allowEditing={false} />}
                      </Datagrid>
                    </>
                  )}
                  <Toolbar>
                    <Item location='after' widget='dxButton' showText='inMenu' locateInMenu='auto'>
                      <span>
                        <i style={{ backgroundImage: `url(${ordCounterExams})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', display: 'inline-block', width: '20px', height: '20px', verticalAlign: 'middle' }}></i>
                        {formatMessage('OrdTotalStudiesAdded')}: {addedStudies.length}
                      </span>
                    </Item>
                  </Toolbar>
                </div>
              </div>
            </ScrollView>
            {!(isSmall || isXSmall) && samplePanelVisible && (
              <div style={{ flex: '0 0 250px', minWidth: 0, overflow: 'auto' }}>
                <SampleTubesPanel data={sampleTubesData} />
              </div>
            )}
          </div>
          <div>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdInvoiceItem'}>
              {formatMessage('OrdInvoiceItem')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdDeleteSelectedStudy'}>
              {formatMessage('OrdDeleteSelectedStudy')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdMarkSelectedStudyAsPending'}>
              {selectedStudie?.state ? formatMessage('OrdMarkSelectedStudyAsPending') : formatMessage('OrdMarkSelectedStudyAsReceived')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdPrintBarcode'}>
              Imprimir etiquetas para la orden
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdReferSelectedStudy'}>
              {formatMessage('OrdReferSelectedStudy')}
            </Tooltip>
            <Tooltip showEvent='mouseenter' hideEvent='mouseleave' position={'top'} target={'#OrdViewAdditionalInfForStudy'}>
              {formatMessage('OrdViewAdditionalInfForStudy')}
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Popup de muestras para dispositivos pequeños */}
      {(isSmall || isXSmall) && (
        <PopupComponent visible={muestrasPopupVisible} onHiding={() => setMuestrasPopupVisible(false)} dragEnabled={false} hideOnOutsideClick={true} showCloseButton={true} showTitle={true} title='Muestras' width={isXSmall ? '100%' : 400} height='auto' fullScreen={isXSmall}>
          <SampleTubesPanel data={sampleTubesData} />
        </PopupComponent>
      )}
    </>
  );
};

export default Index;

// Función para renderizar el chip de laboratorio de referencia
const renderLabRefChip = (
  { data }: any,
  cmbLabRef: any[],
  onChipClick?: (studyId: number, studyName: string, currentLabRefId: number | null) => void,
  hasPermission?: boolean,
  temporalRemByStudy?: Map<number, TemporalRemInfo>,
  savedLabRefByStudy?: Map<number, number | null>
) => {
  const temporalRem = temporalRemByStudy?.get(data.Es_Id);

  // El backend prioriza el laboratorio guardado en la orden sobre el de la remisión
  // temporal: si el estudio ya quedó guardado con otro laboratorio, conserva el suyo y la
  // remisión no llega a aplicarse.
  const savedLabRefId = savedLabRefByStudy?.get(data.Es_Id);
  const temporalIgnored = !!temporalRem && savedLabRefId != null && savedLabRefId !== temporalRem.labRefId;

  return (
    <LabRefChip
      labRefId={data.Es_Lr_Id}
      labRefData={cmbLabRef}
      size='small'
      opacity={0.9}
      temporalLabRefId={temporalRem?.labRefId ?? null}
      temporalEndDate={temporalRem?.endDate ?? null}
      temporalIgnored={temporalIgnored}
      onClick={hasPermission && onChipClick ? () => onChipClick(data.Es_Id, data.Es_Nombre, data.Es_Lr_Id) : undefined}
      clickable={hasPermission || false}
    />
  );
};
